import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type {
  DashboardCard,
  DashboardDrillDown,
  DashboardVisibility,
} from '@catalyst-psa/shared';
import { DASHBOARD_VISIBILITY_LABELS } from '@catalyst-psa/shared';

import { mutationError } from '../../api/client.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { Icon } from '../../components/ui/Icon.js';
import { Modal } from '../../components/ui/Modal.js';
import { SelectField, TextAreaField, TextField } from '../../components/ui/Field.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { formatDateTime } from '../../utils/date.js';
import { CardBuilderModal } from './CardBuilderModal.js';
import { DashboardCardView } from './DashboardCardView.js';
import { DashboardGrid, type LayoutEntry } from './DashboardGrid.js';
import { DrillDownModal } from './DrillDownModal.js';
import { useCanManageShared } from './DashboardsPage.js';
import {
  useDashboard,
  useDashboardDrillDown,
  useDashboardRun,
  useDeleteDashboard,
  useDuplicateDashboardCard,
  useMemberPreferences,
  useRemoveDashboardCard,
  useSaveDashboardLayout,
  useSetPinnedDashboard,
  useUpdateDashboard,
} from './api.js';

export function DashboardDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const detailQuery = useDashboard(id);
  const runQuery = useDashboardRun(id);
  const saveLayout = useSaveDashboardLayout();
  const removeCard = useRemoveDashboardCard();
  const duplicateCard = useDuplicateDashboardCard();
  const drillDown = useDashboardDrillDown();
  const preferences = useMemberPreferences();
  const setPinned = useSetPinnedDashboard();
  const isPinned = preferences.data?.defaultDashboardId === id;

  const [editing, setEditing] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingCard, setEditingCard] = useState<DashboardCard | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [cardToRemove, setCardToRemove] = useState<DashboardCard | null>(null);
  const [drillCard, setDrillCard] = useState<DashboardCard | null>(null);
  const [drillData, setDrillData] = useState<DashboardDrillDown | undefined>(undefined);

  const dashboard = detailQuery.data;
  const cards = useMemo(() => dashboard?.cards ?? [], [dashboard]);

  const resultsByCard = useMemo(
    () => new Map((runQuery.data?.cards ?? []).map((card) => [card.cardId, card])),
    [runQuery.data],
  );

  // Leaving edit mode should never strand an open builder.
  useEffect(() => {
    if (!editing) setBuilderOpen(false);
  }, [editing]);

  const handleLayoutChange = (layout: LayoutEntry[]): void => {
    if (!id) return;
    saveLayout.mutate({ dashboardId: id, input: { layout } });
  };

  const openDrillDown = (card: DashboardCard, groupKey: string | null): void => {
    if (!id) return;
    setDrillCard(card);
    setDrillData(undefined);
    drillDown.mutate(
      { dashboardId: id, cardId: card.id, groupKey },
      { onSuccess: (data) => setDrillData(data) },
    );
  };

  if (detailQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (detailQuery.isError || !dashboard || !id) {
    return (
      <div>
        <PageHeader title="Dashboard" />
        <p className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          This dashboard could not be loaded. It may have been deleted, or it belongs to
          someone else.
        </p>
        <Link to="/dashboards" className="mt-3 inline-block text-sm text-brand-600">
          Back to dashboards
        </Link>
      </div>
    );
  }

  const layoutError = mutationError(saveLayout.error, 'Could not save the layout.');

  return (
    <div>
      <PageHeader
        title={dashboard.name}
        description={dashboard.description ?? undefined}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <span className="hidden text-xs text-slate-500 sm:inline">
              {runQuery.data ? `Updated ${formatDateTime(runQuery.data.ranAt)}` : ''}
            </span>
            <Button
              variant="secondary"
              onClick={() => setPinned.mutate(isPinned ? null : id)}
              isLoading={setPinned.isPending}
              title={
                isPinned
                  ? 'You land here when you open the PSA. Click to go back to Home.'
                  : 'Land on this dashboard when you open the PSA'
              }
            >
              <Icon
                name="star"
                className={`h-4 w-4 ${isPinned ? 'text-amber-500' : ''}`}
                fill={isPinned ? 'currentColor' : 'none'}
              />
              {isPinned ? 'My home page' : 'Set as home page'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => void runQuery.refetch()}
              isLoading={runQuery.isFetching}
              title="Refresh all cards"
            >
              Refresh
            </Button>
            {dashboard.canEdit ? (
              <>
                <Button
                  variant={editing ? 'primary' : 'secondary'}
                  onClick={() => setEditing((current) => !current)}
                >
                  {editing ? 'Done' : 'Edit'}
                </Button>
                {editing ? (
                  <>
                    <Button
                      onClick={() => {
                        setEditingCard(null);
                        setBuilderOpen(true);
                      }}
                    >
                      Add card
                    </Button>
                    <Button variant="secondary" onClick={() => setSettingsOpen(true)}>
                      Settings
                    </Button>
                  </>
                ) : null}
              </>
            ) : null}
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Badge tone={dashboard.visibility === 'shared' ? 'blue' : 'gray'}>
          {DASHBOARD_VISIBILITY_LABELS[dashboard.visibility]}
        </Badge>
        {dashboard.ownerName ? (
          <span className="text-xs text-slate-500">Owned by {dashboard.ownerName}</span>
        ) : null}
        <Link to="/dashboards" className="ml-auto text-xs font-medium text-brand-600">
          All dashboards
        </Link>
      </div>

      {layoutError ? (
        <p className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {layoutError}
        </p>
      ) : null}

      {editing ? (
        <p className="mb-3 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-xs text-brand-800">
          Drag a card by its title bar to move it, or drag the bottom-right corner to resize.
        </p>
      ) : null}

      {cards.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
          <Icon name="dashboard" className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-3 text-sm font-medium text-slate-900">This dashboard is empty</p>
          <p className="mt-1 text-sm text-slate-500">
            Add a card to start tracking something — a count, a list, or a chart.
          </p>
          {dashboard.canEdit ? (
            <Button
              className="mt-4"
              onClick={() => {
                setEditing(true);
                setEditingCard(null);
                setBuilderOpen(true);
              }}
            >
              Add your first card
            </Button>
          ) : null}
        </div>
      ) : (
        <DashboardGrid
          cards={cards}
          editing={editing}
          onLayoutChange={handleLayoutChange}
          renderCard={(card, handlers) => (
            <DashboardCardView
              card={card}
              data={resultsByCard.get(card.id)}
              isLoading={runQuery.isLoading}
              editing={editing}
              onEdit={() => {
                setEditingCard(card);
                setBuilderOpen(true);
              }}
              onDuplicate={() => duplicateCard.mutate({ dashboardId: id, cardId: card.id })}
              onRemove={() => setCardToRemove(card)}
              onDrillDown={(groupKey) => openDrillDown(card, groupKey)}
              onDragStart={handlers.onDragStart}
              onResizeStart={handlers.onResizeStart}
            />
          )}
        />
      )}

      <CardBuilderModal
        isOpen={builderOpen}
        onClose={() => setBuilderOpen(false)}
        dashboardId={id}
        card={editingCard}
      />

      <DrillDownModal
        isOpen={drillCard !== null}
        onClose={() => {
          setDrillCard(null);
          setDrillData(undefined);
        }}
        title={drillCard?.title ?? ''}
        data={drillData}
        isLoading={drillDown.isPending}
        error={mutationError(drillDown.error, 'Could not load these records.')}
      />

      <ConfirmDialog
        isOpen={cardToRemove !== null}
        title="Remove card"
        message={`Remove "${cardToRemove?.title ?? ''}" from this dashboard?`}
        confirmLabel="Remove"
        destructive
        isConfirming={removeCard.isPending}
        onCancel={() => setCardToRemove(null)}
        onConfirm={() => {
          if (!cardToRemove) return;
          removeCard.mutate(
            { dashboardId: id, cardId: cardToRemove.id },
            { onSuccess: () => setCardToRemove(null) },
          );
        }}
      />

      <DashboardSettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        dashboardId={id}
        name={dashboard.name}
        description={dashboard.description}
        visibility={dashboard.visibility}
        onDeleted={() => navigate('/dashboards')}
      />
    </div>
  );
}

function DashboardSettingsModal({
  isOpen,
  onClose,
  dashboardId,
  name: initialName,
  description: initialDescription,
  visibility: initialVisibility,
  onDeleted,
}: {
  isOpen: boolean;
  onClose: () => void;
  dashboardId: string;
  name: string;
  description: string | null;
  visibility: DashboardVisibility;
  onDeleted: () => void;
}): JSX.Element {
  const updateDashboard = useUpdateDashboard();
  const deleteDashboard = useDeleteDashboard();
  const canManageShared = useCanManageShared();
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription ?? '');
  const [visibility, setVisibility] = useState<DashboardVisibility>(initialVisibility);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const resetUpdate = updateDashboard.reset;

  useEffect(() => {
    if (!isOpen) return;
    resetUpdate();
    setName(initialName);
    setDescription(initialDescription ?? '');
    setVisibility(initialVisibility);
  }, [isOpen, initialName, initialDescription, initialVisibility, resetUpdate]);

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    updateDashboard.mutate(
      {
        id: dashboardId,
        input: {
          name: name.trim(),
          description: description.trim() || null,
          visibility,
        },
      },
      { onSuccess: onClose },
    );
  };

  const error = mutationError(updateDashboard.error, 'Could not save the dashboard.');

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Dashboard settings"
        footer={
          <div className="flex items-center justify-between gap-2">
            <Button variant="ghost" type="button" onClick={() => setConfirmDelete(true)}>
              Delete dashboard
            </Button>
            <div className="flex items-center gap-2">
              <Button variant="secondary" type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                form="dashboard-settings"
                disabled={!name.trim()}
                isLoading={updateDashboard.isPending}
              >
                Save
              </Button>
            </div>
          </div>
        }
      >
        <form id="dashboard-settings" onSubmit={handleSubmit} className="space-y-4">
          {error ? (
            <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {error}
            </p>
          ) : null}
          <TextField
            label="Name"
            required
            value={name}
            maxLength={200}
            onChange={(e) => setName(e.target.value)}
          />
          <TextAreaField
            label="Description"
            rows={2}
            value={description}
            maxLength={1000}
            onChange={(e) => setDescription(e.target.value)}
          />
          <SelectField
            label="Visibility"
            value={visibility}
            onChange={(e) => setVisibility(e.target.value as DashboardVisibility)}
            options={[
              { value: 'personal', label: 'Personal — only I can see it' },
              ...(canManageShared || initialVisibility === 'shared'
                ? [{ value: 'shared', label: 'Shared — everyone with dashboard access' }]
                : []),
            ]}
          />
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={confirmDelete}
        title="Delete dashboard"
        message="This removes the dashboard and all of its cards. This cannot be undone."
        confirmLabel="Delete"
        destructive
        isConfirming={deleteDashboard.isPending}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() =>
          deleteDashboard.mutate(dashboardId, {
            onSuccess: () => {
              setConfirmDelete(false);
              onClose();
              onDeleted();
            },
          })
        }
      />
    </>
  );
}
